import { useEffect, useRef } from 'react';
import { AcsButton, AcsStatusBadge } from '../components';
import { useI18n } from '../i18n/I18nProvider';
import type { PolicyDetail } from '../api/client';

/**
 * Anteprima di un'informativa, resa come la vede il visitatore.
 *
 * Serve al momento in cui conta davvero: prima di pubblicare. Da qui in poi il
 * testo diventa immutabile, quindi rileggerlo nella forma in cui verrà mostrato
 * — non nel campo di modifica — è l'ultima occasione utile per accorgersi di
 * qualcosa.
 *
 * Il contenuto è reso come testo semplice, mai come HTML: un'informativa non
 * deve poter introdurre markup nella pagina (§11.4).
 */

/** Segnaposto rimasti da colmare: è ciò che si cerca in un'ultima rilettura. */
const PLACEHOLDER = /\[DA COMPLETARE[^\]]*\]/g;

export function countPlaceholders(content: string): number {
  return content.match(PLACEHOLDER)?.length ?? 0;
}

export function PolicyPreviewDialog({
  policy,
  onClose,
  onPublish,
}: {
  policy: PolicyDetail;
  onClose: () => void;
  onPublish?: () => void;
}) {
  const { t } = useI18n();
  const closeRef = useRef<HTMLButtonElement>(null);
  const placeholders = countPlaceholders(policy.content);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="kiosk-dialog__backdrop" role="presentation" onClick={onClose}>
      <div
        className="kiosk-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={policy.title}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 'var(--acs-space-4)',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 className="kiosk-dialog__title">{policy.title}</h2>
            <p className="kiosk-dialog__meta">
              {policy.version} · {policy.language.toUpperCase()}
              {policy.publishedAt
                ? ` · ${new Date(policy.publishedAt).toLocaleDateString()}`
                : ''}
            </p>
          </div>
          <AcsStatusBadge>
            {policy.published ? t.policies.published : t.policies.draft}
          </AcsStatusBadge>
        </div>

        {/*
          Un'informativa con segnaposto non è pronta. Segnalarlo qui, dove la si
          rilegge prima di pubblicare, è più utile che scoprirlo dopo — quando
          per correggerla servirebbe una nuova versione.
        */}
        {placeholders > 0 ? (
          <p
            role="alert"
            style={{
              background: 'var(--acs-color-highlight)',
              color: 'var(--acs-color-highlight-contrast)',
              padding: 'var(--acs-space-3)',
              borderRadius: 'var(--acs-radius-md)',
            }}
          >
            {t.policies.placeholdersFound.replace('{n}', String(placeholders))}
          </p>
        ) : null}

        {policy.published ? (
          <p style={{ color: 'var(--acs-color-text-muted)' }}>
            {t.policies.publishedImmutable} · {t.policies.hash}: {policy.hash.slice(0, 16)}…
          </p>
        ) : (
          <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.policies.draftExplain}</p>
        )}

        {/* Testo semplice, mai `dangerouslySetInnerHTML`. */}
        <div className="kiosk-dialog__body">{policy.content}</div>

        <div style={{ display: 'flex', gap: 'var(--acs-space-3)', flexWrap: 'wrap' }}>
          <AcsButton ref={closeRef} variant="secondary" onClick={onClose}>
            {t.policies.closePreview}
          </AcsButton>
          {!policy.published && onPublish ? (
            <AcsButton onClick={onPublish}>{t.policies.publish}</AcsButton>
          ) : null}
        </div>
      </div>
    </div>
  );
}
